'use client';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import React, { SVGProps, useMemo } from 'react';

import {
  AbgTypography,
  CheckIcon,
  CloseIcon,
  formatMoney,
} from '@abg/kit-base';
import { AbgSxProps } from '@abg/typings';

import { useAnalyticsGeneralEngagement } from '../../../../shared/analytics/hooks/use-analytics-general-engagement';
import { useTranslationContext } from '../../../../shared/context';
import { AncillariesI18nKeys } from '../../../../shared/types';
import { mapTableRows } from '../utils/map-table-rows';

import { splitBundlesByIncluded } from './utils/split-bundles-by-included';
import { ProtectionsComparisonMobileProps } from './types';

export const ProtectionsComparisonMobile = ({
  styleParts = {},
  bundles,
  onClose,
  onBundleSelected,
}: ProtectionsComparisonMobileProps) => {
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();

  const { emitEvent: emitGeneralEngagementEvent } =
    useAnalyticsGeneralEngagement();

  const { bundlesWithIncluded, bundlesWithoutIncluded } = useMemo(
    () => splitBundlesByIncluded(bundles),
    [bundles],
  );

  const rows = useMemo(
    () => mapTableRows(bundlesWithIncluded),
    [bundlesWithIncluded],
  );

  const handleSelection = (bundleCode: string) => {
    onBundleSelected(bundleCode);
    onClose();
  };

  return (
    <Dialog
      open
      sx={{ ...styleParts.dialogMobile }}
      data-testid="protections-comparison-compare-bundles-card-container"
    >
      <Box sx={{ ...styleParts.dialogTitleWrapperMobile }}>
        <DialogTitle
          sx={{ ...styleParts.dialogTitleMobile }}
          data-testid="protections-comparison-compare-bundles-card-title"
        >
          {t(i18nKeys.compareBundlesLabel)}
        </DialogTitle>
        <DialogActions sx={{ padding: 0 }}>
          <CloseIcon
            data-testid="protections-comparison-close-btn"
            sx={{ ...styleParts.closeDialogIconDimensions }}
            strokeWidth={
              (styleParts.closeDialogIcon as SVGProps<SVGPathElement>)
                ?.strokeWidth
            }
            onClick={(event) => {
              emitGeneralEngagementEvent(
                event.currentTarget,
                'ProtectionsComparisonMobile',
              );
              onClose();
            }}
          />
        </DialogActions>
      </Box>
      <DialogContent sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            {bundlesWithIncluded.map((bundle) => (
              <Box
                data-testid="protections-comparison-bundle-header"
                key={bundle.title}
                sx={
                  {
                    ...styleParts.headerCellMobile,
                    ...(bundle.recommended
                      ? styleParts.recommendedColumnTopMobile
                      : {}),
                  } as AbgSxProps
                }
              >
                <Box sx={{ ...styleParts.recommendedWrapper }}>
                  {bundle.recommended && (
                    <AbgTypography
                      align="center"
                      sx={{ ...styleParts.recommended }}
                      data-testid="protections-comparison-bundle-recommended"
                    >
                      {t(i18nKeys.recommendedLabel)}
                    </AbgTypography>
                  )}
                </Box>
                <Box>
                  <AbgTypography
                    sx={{ ...styleParts.headerCellTitleMobile }}
                    data-testid="protections-comparison-bundle-name"
                  >
                    {bundle.title}
                  </AbgTypography>
                  <AbgTypography
                    sx={{ ...styleParts.headerCellPriceMobile }}
                    data-testid="protections-comparison-bundle-price"
                  >
                    {formatMoney(Number(bundle.price), {
                      currency: bundle.currencyCode || 'USD',
                    })}{' '}
                    / {t(i18nKeys.dayLabel)}
                  </AbgTypography>
                </Box>
              </Box>
            ))}
          </Box>

          {rows.map((row, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              data-testid="protections-comparison-single-protection-row"
            >
              {row.map((item, colIndex) => (
                <Box
                  key={colIndex}
                  sx={
                    {
                      ...styleParts.rowCellMobile,
                      ...(item?.recommended
                        ? styleParts.recommendedColumnBackground
                        : {}),
                    } as AbgSxProps
                  }
                >
                  {item?.included ? (
                    <CheckIcon
                      data-testid="protections-comparison-item-included-icon"
                      sx={{ ...styleParts.checkItemIconDimensions }}
                      stroke={
                        (styleParts.checkItemIcon as SVGProps<SVGPathElement>)
                          ?.stroke
                      }
                      strokeWidth={
                        (styleParts.checkItemIcon as SVGProps<SVGPathElement>)
                          ?.strokeWidth
                      }
                    />
                  ) : (
                    <CloseIcon
                      data-testid="protections-comparison-item-excluded-icon"
                      sx={{ ...styleParts.closeItemIconDimensions }}
                      stroke={
                        (styleParts.closeItemIcon as SVGProps<SVGPathElement>)
                          ?.stroke
                      }
                      strokeWidth={
                        (styleParts.closeItemIcon as SVGProps<SVGPathElement>)
                          ?.strokeWidth
                      }
                    />
                  )}
                  <AbgTypography
                    sx={{ ...styleParts.protectionTitleMobile }}
                    data-testid="protections-comparison-single-protection-name"
                  >
                    {item?.title}
                  </AbgTypography>
                </Box>
              ))}
            </Box>
          ))}

          <Box sx={{ ...styleParts.actionButtonsWrapperMobile }}>
            {bundlesWithIncluded.map((bundle) => (
              <Box
                key={bundle.title}
                sx={
                  {
                    ...styleParts.rowCellActionMobile,
                    ...(bundle.recommended
                      ? styleParts.recommendedColumnBottomMobile
                      : {}),
                  } as AbgSxProps
                }
              >
                <Button
                  data-testid="protections-comparison-add-to-trip-btn"
                  variant="contained"
                  sx={{ ...styleParts.buttonMobile }}
                  onClick={() => handleSelection(bundle.code)}
                >
                  {t(i18nKeys.addToTripMobileLabel)}
                </Button>
              </Box>
            ))}
          </Box>
        </Box>

        {bundlesWithoutIncluded.map((bundle) => (
          <Box
            key={bundle.title}
            sx={{ ...styleParts.notIncludedProtectionsMobile }}
          >
            <Box sx={{ ...styleParts.notIncludedProtectionsWrapperMobile }}>
              <AbgTypography
                sx={{ ...styleParts.notIncludedProtectionsTitleMobile }}
                data-testid="protections-comparison-bundle-without-included-items"
              >
                {bundle.title}
              </AbgTypography>
              <AbgTypography
                sx={{ ...styleParts.notIncludedProtectionsPriceMobile }}
              >
                {formatMoney(Number(bundle.price), {
                  currency: bundle.currencyCode || 'USD',
                })}{' '}
                / {t(i18nKeys.dayLabel)}
              </AbgTypography>
            </Box>

            <AbgTypography
              sx={{ ...styleParts.notIncludedProtectionsDescriptionMobile }}
            >
              {t(i18nKeys.noProtectionsLabel)}
            </AbgTypography>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};
